import { useEffect, useState } from "react";

/**
 * Общая fetch-логика отчётных вьюх (Недельный/Месячный свод, Исключения):
 * query == null → ничего не грузим; смена query → loading + новый запрос.
 *
 * `cancelled`-guard закрывает гонку, которая была в скопированных эффектах
 * до рефакторинга: медленный ответ на СТАРЫЙ query мог перезатереть данные
 * уже отрисованного нового (быстрое переключение фильтров).
 *
 * `fetcher` должен быть стабильной ссылкой (модульная функция из api/client) —
 * иначе эффект будет перезапускаться на каждый рендер.
 */
export function useReportFetch<Q, R>(
  query: Q | null,
  fetcher: (q: Q) => Promise<R>,
): { report: R | null; loading: boolean; error: string | null } {
  const [report, setReport] = useState<R | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!query) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher(query)
      .then((data) => {
        if (!cancelled) setReport(data);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, fetcher]);

  return { report, loading, error };
}
